import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { startCase } from 'lodash';
import { useAndromeda } from '../../../modules/Andromeda';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginBottom: '20px',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(4),
  },
  filterContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  filter: {
    width: theme.spacing(30),
    margin: 0,
  },
}));

export const AndromedaJobsFilters = ({
  applyFilters,
  loading,
  filters,
  setFilters,
}) => {
  const classes = useStyles();
  const { getJobExplorerFilterOptions } = useAndromeda();

  /*
   * This initial state value makes sure that the mapping function inside the agent filter <Select> components renders a placeholder <MenuItem> while agents are fetched.
   * The loadAgents() function run on component mount appends any existing agents to the fetched list,
   *   so even non-existent agents will have a value in the rendered list of <MenuItem>'s to keep the user experience polished
   *
   * "all" always has a corresponding <MenuItem> that is always rendered before mapping through agents, so we can initialize to a blank array in that case
   *
   */
  const [agents, setAgents] = useState(
    filters?.agent === 'all' ? [] : [filters?.agent]
  );
  const [types, setTypes] = useState(
    filters?.type === 'all' ? [] : [filters?.type]
  );

  const setAgent = (agent) => {
    setFilters((prev) => ({ ...prev, agent }));
  };

  const setType = (type) => {
    setFilters((prev) => ({ ...prev, type }));
  };

  const setParticipantId = (participantId) => {
    setFilters((prev) => ({ ...prev, participantId }));
  };

  useEffect(() => {
    const loadFilterOptions = async () => {
      const options = await getJobExplorerFilterOptions();
      if (options) {
        setAgents((prev) => {
          const filteredAgents = options.agents.filter(
            (x) => x !== filters?.agent
          );
          return [...filteredAgents, ...prev];
        });
        setTypes((prev) => {
          const filteredTypes = options.jobTypes.filter(
            (x) => x !== filters?.type
          );
          return [...filteredTypes, ...prev];
        });
      }
    };

    loadFilterOptions();
  }, []);

  return (
    <section className={classes.wrapper}>
      <Paper className={classes.content}>
        <Typography variant="h6">Filters</Typography>
        <div className={classes.filterContainer}>
          <TextField
            label="Participant Id"
            name="participant-id-filter"
            variant="outlined"
            value={filters.participantId}
            className={classes.filter}
            onChange={(event) => {
              setParticipantId(event.target.value);
            }}
          />
          <FormControl variant="outlined">
            <InputLabel variant="outlined" htmlFor="agent-filter">
              Agent
            </InputLabel>
            <Select
              label="Agent"
              name="agent-filter"
              variant="outlined"
              value={filters.agent}
              className={classes.filter}
              onChange={(event) => setAgent(event.target.value)}
            >
              <MenuItem value="all">All Agents</MenuItem>
              {agents.map((agent, idx) => (
                <MenuItem key={idx} value={agent}>
                  {agent}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl variant="outlined">
            <InputLabel variant="outlined" htmlFor="type-filter">
              Type
            </InputLabel>
            <Select
              label="Type"
              name="type-filter"
              variant="outlined"
              value={filters.type}
              className={classes.filter}
              onChange={(event) => setType(event.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              {types.map((type, idx) => (
                <MenuItem key={idx} value={type}>
                  {startCase(type)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <Button disabled={loading} onClick={() => applyFilters()}>
          Apply
        </Button>
      </Paper>
    </section>
  );
};
